import {useQuery} from '@tanstack/react-query';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {Employee, HomeAgendaItem, HomeAgendaResponse, HomeService} from 'client';
import {t} from 'i18next';
import {atom, useAtomValue} from 'jotai';
import {useMemo} from 'react';
import {getService} from 'utilities';

import {frequentPeopleAtom} from './frequentPeople';

export type MeetingAttendee = {
  key: string;
  name: string;
  email?: string | null;
  /** Matched CompanyOS employee; absent for guests outside the company. */
  person?: Employee;
  /** You've worked with them: your reporting line, team, reports or Frequent. */
  known: boolean;
  isOrganizer?: boolean;
};

export type Meeting = {
  id: string;
  title: string;
  start: number;
  end: number;
  location?: string | null;
  joinUrl?: string | null;
  attendeeCount: number;
  /** Everyone but you, people new to you first. */
  attendees: MeetingAttendee[];
};

const HOUR_MS = 60 * 60 * 1000;
const COMING_UP_HOURS = 36;
const MAX_MEETINGS = 3;

const byNewToYouFirst = (a: MeetingAttendee, b: MeetingAttendee) =>
  Number(a.known) - Number(b.known) || Number(b.isOrganizer) - Number(a.isOrganizer);

/**
 * To try the box without meetings: `?search_meetings=none` (a linked calendar
 * with nothing coming up) or `?search_meetings=unlinked` (no calendar linked).
 * Read each time search opens; it doesn't stick.
 */
export const meetingsPreviewAtom = atom<'none' | 'unlinked' | null>(null);

const useAgenda = () =>
  useQuery({
    queryKey: ['home', 'agenda'],
    queryFn: getService(HomeService).agendaApiV1HomeAgendaGet,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

/**
 * Whether the agenda has loaded, and whether it says no calendar is linked
 * (so there can't be meetings until one is).
 */
export const useCalendarStatus = () => {
  const preview = useAtomValue(meetingsPreviewAtom);
  const {data: agenda, isFetched} = useAgenda();
  if (preview) return {loaded: true, notLinked: preview === 'unlinked'};
  return {
    loaded: isFetched,
    notLinked: agenda?.calendar === HomeAgendaResponse.calendar.NOT_LINKED,
  };
};

/**
 * Your next few meetings (the next 36 hours, up to three) with who's in them,
 * from `/api/v1/home/agenda`: timed meetings that have someone besides you,
 * not company events or all-day blocks. Empty while it loads, and when your
 * calendar has nothing or isn't linked.
 */
export const useMeetings = (): Meeting[] => {
  const {employee, reportingLine, teamMembers, directReports} = useAtomValue(loggedInEmployeeAtom);
  const frequent = useAtomValue(frequentPeopleAtom);
  const preview = useAtomValue(meetingsPreviewAtom);
  const {data: agenda} = useAgenda();

  return useMemo(() => {
    if (preview) return [];
    const known = new Set<string>([
      ...reportingLine.map(({ldap}) => ldap),
      ...teamMembers.map(({ldap}) => ldap),
      ...directReports.map(({ldap}) => ldap),
      ...frequent.map(({ldap}) => ldap),
    ]);
    const now = Date.now();

    return (agenda?.items ?? [])
      .filter((item: HomeAgendaItem) => item.source !== 'company' && !item.all_day)
      .filter((item) => item.end > now && item.start < now + COMING_UP_HOURS * HOUR_MS)
      .map((item): Meeting => {
        const others = (item.attendees ?? []).filter(
          (attendee) => !attendee.is_self && attendee.employee?.ldap !== employee.ldap
        );
        return {
          id: item.id,
          title: item.title,
          start: item.start,
          end: item.end,
          location: item.location,
          joinUrl: item.join_url,
          attendeeCount: item.attendee_count ?? others.length,
          attendees: others
            .map((attendee, index): MeetingAttendee => {
              const person = attendee.employee ? (attendee.employee as Employee) : undefined;
              return {
                key: person?.ldap ?? attendee.email ?? `guest-${index}`,
                name:
                  person?.name ?? attendee.name ?? attendee.email ?? t('search_box_attendee_guest'),
                email: attendee.email,
                person,
                known: person ? known.has(person.ldap) : false,
                isOrganizer: attendee.is_organizer,
              };
            })
            .sort(byNewToYouFirst),
        };
      })
      .filter((meeting) => meeting.attendees.length > 0)
      .sort((a, b) => a.start - b.start)
      .slice(0, MAX_MEETINGS);
  }, [preview, agenda, frequent, employee, reportingLine, teamMembers, directReports]);
};

export const timeOfDay = (at: number) =>
  new Date(at).toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'});

export const formatMeetingTime = (start: number, now = Date.now()) => {
  const minutes = Math.round((start - now) / 60000);
  if (minutes <= 0) return t('search_box_meeting_now');
  if (minutes < 60) return t('search_box_meeting_in_minutes', {count: minutes});
  const time = timeOfDay(start);
  const sameDay = new Date(start).toDateString() === new Date(now).toDateString();
  return sameDay ? time : t('search_box_meeting_tomorrow_at', {time});
};

/**
 * When, as a list column: one line when it's close ("In 36 min", "Now"),
 * else the day over the time ("Tomorrow" / "5:30 PM").
 */
export const meetingWhen = (start: number, now = Date.now()): string[] => {
  const minutes = Math.round((start - now) / 60000);
  if (minutes < 60) return [formatMeetingTime(start, now)];
  const sameDay = new Date(start).toDateString() === new Date(now).toDateString();
  return [sameDay ? t('today') : t('search_box_meeting_tomorrow'), timeOfDay(start)];
};
